import DatePicker, { ReactDatePickerProps } from 'react-datepicker';
import { forwardRef } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import ReactTooltip from 'react-tooltip';
import { Header } from '@tanstack/react-table';
import { Deployment } from 'interfaces/deployment';
import { useTranslation } from 'react-i18next';
import './DateRangeInput.scss';

/**
 * @description Custom type for date range picker onChange function
 */
type TypeMyOnchange = true;

/**
 * @description Type definition for the onChange function from react-datepicker
 */
type onChangeFn = ReactDatePickerProps<never, TypeMyOnchange>['onChange'];

/**
 * @description Type definition for the date range input value
 * Represents an array with two date objects [startDate, endDate]
 */
type DateRangeInputType = Parameters<onChangeFn>[0];

/**
 * @description Component for filtering deployment history by date range with integrated date picker.
 *
 * @SADtrace SAD-003-6.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {Object} props - Component props
 * @param {Header<Deployment, unknown>} props.header - The table header object from tanstack table
 *
 * @returns {JSX.Element} Rendered component
 */
const DateRangeInput = ({
  header,
}: {
  header: Header<Deployment, unknown>;
}) => {
  const { t } = useTranslation();

  /**
   * @description Custom input component for the DatePicker
   * Renders a form control with tooltip
   */
  // eslint-disable-next-line react/display-name
  const DatePickerCustomInput = forwardRef<any, any>(
    // eslint-disable-next-line react/prop-types
    ({ value, onClick }, ref) => (
      <>
        <input
          className={'form-control form-control-sm rounded'}
          onClick={onClick}
          readOnly
          ref={ref}
          defaultValue={value}
          placeholder={t(`deployment.table.placeholder_${header.column.id}`)}
          data-tip={value}
          style={{ fontSize: '.875rem' }}
        />
        <ReactTooltip effect="solid" place="bottom" className="toolTip" />
      </>
    ),
  );

  /**
   * @description Current date range value from the column filter
   * Defaults to [null, null] if no filter is set
   */
  const dates: DateRangeInputType =
    (header.column.getFilterValue() as DateRangeInputType) ?? [null, null];

  /**
   * @description Handles date range changes and updates the column filter value.
   *
   * @SADtrace SAD-003-6.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {DateRangeInputType} dates - Array containing [startDate, endDate]
   */
  // v8 ignore start -- WAIVER(THIRDPARTY): callback invoked by react-datepicker's calendar UI on range selection; jsdom unit tests cannot drive the calendar popup; date filtering verified via E2E
  const onChange: onChangeFn = (dates) => {
    header.column.setFilterValue(dates);
  };
  // v8 ignore stop -- WAIVER(THIRDPARTY)

  return (
    <DatePicker<never, TypeMyOnchange>
      selectsRange
      selected={dates[0]}
      onChange={onChange}
      startDate={dates[0]}
      endDate={dates[1]}
      customInput={<DatePickerCustomInput />}
      isClearable={true}
      maxDate={new Date()}
    />
  );
};

export default DateRangeInput;
